import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import visualizationService from "../services/visualization";

const breakpointList = ['lg', 'md', 'sm', 'xs']
const breakpointWidth = {
  lg: 12,
  md: 10,
  sm: 6,
  xs: 4
}

const initialState = {
  list: [],
  data: {},
  layouts: {
    lg: [],
    md: [],
    sm: [],
    xs: [],
  },
  schedule: [],
  breakpoint: "lg",
  color: ['#7B53EF', '#F87289', '#FBBE55', '#B5C2FA', '#72E198'],
  loading: false
};

export const getList = createAsyncThunk(
  "visualization/list",
  async () => {
    const res = await visualizationService.getVisualizataionList();
    return res.data;
  }
)

export const createVisualization = createAsyncThunk(
  "visualization/create",
  async (info) => {
    const res = await visualizationService.createVisualization(info);
    return res.data;
  }
)

export const updateVisualization = createAsyncThunk(
  "visualization/update",
  async (info) => {
    const {id} = info;
    const res = await visualizationService.updateVisualization(info);
    return {
      id,
      ...res.data
    };
  }
)

export const deleteVisualization = createAsyncThunk(
  "visualization/delete",
  async (info) => {
    const {id} = info;
    const res = await visualizationService.deleteVisualization(info);
    return {
      id,
      success:res.data
    };
  }
)

export const setVisualization = createAsyncThunk(
  "visualization/setVisualization", 
  async (info) => {
  const {id, title, type, color, db, prompt} = info;

  const res = await visualizationService.setVisualization({name: db, prompt});
  if(id) return {...res.data, id, title, type, color, db};
  else return {...res.data, title, db, status: true}
})

export const saveVisualization = createAsyncThunk(
  "visualization/save",
  async (info) => {
    const res = await visualizationService.saveVisualization(info);
    return res.data;
  }
)

export const getVisualization = createAsyncThunk(
  "visualization/get",
  async (info) => {
    const res = await visualizationService.getVisualization(info);
    return res.data;
  }
)

export const scheduleVisualization = createAsyncThunk(
  "visualization/schedule",
  async (info) => {
    const res = await visualizationService.scheduleVisualization(info);
    return res.data;
  }
)

export const loadSchedule = createAsyncThunk(
  "visualization/load",
  async (info) => {
    const res = await visualizationService.loadSchedule(info);
    return res.data;
  }
)

export const updateScheduleVisualization = createAsyncThunk(
  "visualization/schedule/update",
  async (info) => {
    const {scheduleID} = info;
    const res = await visualizationService.updateScheduleVisualization(info);
    return {
      schedule_id: scheduleID,
      ...res.data
    };
  }
)

export const deleteSchedule = createAsyncThunk(
  "visualization/schedule/delete",
  async (info) => {
    const {scheduleID} = info;
    const res = await visualizationService.deleteSchedule(info);
    return {
      scheduleID,
      success: res.data};
  }
)

const visualizationSlice = createSlice({
  name: "visualization",
  initialState,
  reducers: {
    setLayout: (state, action) => {
      const {breakpoint, layout} = action.payload;
      state.layouts[breakpoint] = layout
    },
    setBreakPoint: (state, action) => {
      state.layouts.breakpoint = action.payload
    },
    setTitle: (state, action) => {
      const {item, title} = action.payload;
      state.data[item].title = title;
    },
    setPrompt: (state, action) => {
      const {item, prompt} = action.payload;
      state.data[item].prompt = prompt;
    },
    setColor: (state, action) => {
      const {item, color} = action.payload;
      state.data[item].color = color;
    },
    setType: (state, action) => {
      const {item, type} = action.payload;
      state.data[item].type = type;
    },
    setMode: (state, action) => {
      const {item, mode} = action.payload;
      state.data[item].mode = mode;
    },
    setLoading: (state, action) => {
      state.loading = true;
    },
    removeVisualization: (state, action) => {
      const {item} = action.payload;
      const tempData = state.data;
      if(tempData.hasOwnProperty(item)) delete tempData[item];
      const layoutData = breakpointList.map(breakpoint => (state.layouts[breakpoint].filter(element => element.i !== item)));
      const newLayout = {
        lg: layoutData[0],
        md: layoutData[1],
        sm: layoutData[2],
        xs: layoutData[3],
      }
      state.layouts = newLayout
    },
 },
  extraReducers: (builder) => {
    builder.addCase(getList.fulfilled, (state, action) => {
      const data = action.payload
      state.list = data
    })
    builder.addCase(createVisualization.fulfilled, (state, action) => {
      const {id} = action.payload
      if(id) state.list.push(action.payload)
    })
    builder.addCase(deleteVisualization.fulfilled, (state, action) => {
      const {id, success} = action.payload
      if(success) {
        const newList = state.list.filter(item => item.id !== id)
        state.list = newList
      } 
    })
    builder.addCase(updateVisualization.fulfilled, (state, action) => {
      const {id, name, created_at} = action.payload
      if(name) {
        const newList = state.list.map(item => {
          if(item.id === id) 
            return {
              id,
              name,
              created_at
            }
          else 
            return item;
        })
        state.list = newList
      }
    })
    builder.addCase(setVisualization.fulfilled, (state, action) => {
      let {id, title, prompt, sql_query: query, result: data, type, color, db, status} = action.payload;
      type = type || 'bar';
      color = color || state.color;
      const newGraph = {
        type,
        title,
        color,
        data,
        query,
        prompt,
        db,
        mode: 'graph'
      }
      state.data[id] = newGraph;

      // If the new dahsboard is added:
      if(status) {
        breakpointList.forEach(breakpoint => {
          const layout = state.layouts[breakpoint];
          let maxX = 0, maxY = 0, maxItem;
          layout.forEach(item => { if(item.y > maxY) maxY = item.y })
          layout.forEach(item => { if(maxY === item.y && maxX <= item.x) maxItem = item })
          
          const corner = maxItem ? {x: maxItem.x+maxItem.w, y: maxItem.y + maxItem.h} : {x: 0, y: 0}
          let newLayout;
          if(breakpoint === 'xs')
            newLayout = {
              x: 0, 
              y: corner.y,
              w: 4,
              h: 2,
              i: id
          }
          else if(corner.x <= breakpointWidth[breakpoint]/2) 
            newLayout = {
              x: corner.x, 
              y: maxItem ? maxItem.y:0,
              w: breakpointWidth[breakpoint]/2,
              h: Math.ceil(breakpointWidth[breakpoint]/4),
              i: id
            };
          else
            newLayout = {
              x: 0,
              y: corner.y,
              w: breakpointWidth[breakpoint]/2,
              h: Math.ceil(breakpointWidth[breakpoint]/4),
              i: id
            }
          state.layouts[breakpoint].push(newLayout);
        })
      }

      state.loading = false;
    }) 
    builder.addCase(saveVisualization.fulfilled, (state, action) => {
    })
    builder.addCase(getVisualization.fulfilled, (state, action) => {
      const {layouts, data} = action.payload;

      if(layouts && layouts !== "") 
        state.layouts = JSON.parse(layouts)
      else
        state.layouts = {
          lg: [],
          md: [],
          sm: [],
          xs: [],
        }
      if(data && data !== "") 
        state.data = JSON.parse(data)
      else
        state.data = {}
    })
    builder.addCase(scheduleVisualization.fulfilled, (state, action) => {
      
    })
    builder.addCase(loadSchedule.fulfilled, (state, action) => {
      state.schedule = action.payload
    })
    builder.addCase(updateScheduleVisualization.fulfilled, (state, action) => {
      const {schedule_id, id, title, recurrence, day, time, destinations} = action.payload;
      if(id) {
        const newSchedule = state.schedule.map(item => {
          if(item.schedule_id === schedule_id) 
            return {
              schedule_id, 
              id, 
              title, 
              recurrence, 
              day, 
              time, 
              destinations
            }
          else
            return item;
        })

        state.schedule = newSchedule
      }
    })
    builder.addCase(deleteSchedule.fulfilled, (state, action) => {
      const {success, scheduleID} = action.payload;
      if(success) {
        const newSchedule = state.schedule.filter(item => item.schedule_id !== scheduleID);
        state.schedule = newSchedule;
      }
    })
  }
});

export const {
  setLayout,
  setColor,
  setLoading,
  setTitle,
  setPrompt,
  setType,
  setMode,
  setBreakPoint,
  removeVisualization
} = visualizationSlice.actions;

const { reducer } = visualizationSlice;
export default reducer;